import { jwtDecode } from "jwt-decode";

type AuthPayload = {
  userId?: string | number;
  userType?: string;
  companyId?: string | number;
  permissions?: string[] | string;
  isOwner?: boolean;
};

export function getAuthPayload(): AuthPayload {
  const token = localStorage.getItem("authUser");

  if (!token) {
    return {};
  }

  try {
    const parsedToken = JSON.parse(token);
    return jwtDecode<AuthPayload>(parsedToken);
  } catch {
    try {
      return jwtDecode<AuthPayload>(token);
    } catch {
      return {};
    }
  }
}

export function hasPermission(requiredPermission?: string | string[]) {
  if (!requiredPermission) {
    return true;
  }

  const auth = getAuthPayload();
  const permissions = (Array.isArray(auth.permissions)
    ? auth.permissions
    : typeof auth.permissions === "string"
      ? [auth.permissions]
      : []
  ).map((permission) => String(permission).trim());
  const required = Array.isArray(requiredPermission)
    ? requiredPermission
    : [requiredPermission];

  if (isContractorUser(auth.userType) && auth.isOwner) {
    return true;
  }

  return permissions.includes("ALL") || required.some((permission) => permissions.includes(permission));
}

export function isContractorUser(userType?: string) {
  return userType === "contractor" || userType === "testContractor";
}

export function hasContractPermission(requiredPermission?: string | string[]) {
  const auth = getAuthPayload();
  
  return isContractorUser(auth.userType) && hasPermission(requiredPermission);
}

export function isSameContractCompany(record: any) {
  const auth = getAuthPayload();

  if (!record || !isContractorUser(auth.userType)) {
    return false;
  }

  const recordCompanyId =
    record?.p_company_id ??
    record?.company_id ??
    record?.contract?.pc_company_id ??
    record?.contract_company_id;

  if (recordCompanyId != null && auth.companyId != null) {
    return Number(recordCompanyId) === Number(auth.companyId);
  }

  const recordContractorId =
    record?.p_contractor_id ??
    record?.contractor_id ??
    record?.project_contractor_id;

  if (recordContractorId != null && auth.userId != null) {
    return Number(recordContractorId) === Number(auth.userId);
  }

  return false;
}

export function canAccessContractRecord(record: any, requiredPermission?: string | string[]) {
  const auth = getAuthPayload();

  if (auth.userType === "admin") {
    return true;
  }

  if (!isContractorUser(auth.userType)) {
    return false;
  }

  return isSameContractCompany(record) && hasPermission(requiredPermission);
}

export function isHomeownerContractOwner(record: any) {
  const auth = getAuthPayload();

  if (auth.userType !== "homeowner" && !isContractorUser(auth.userType)) {
    return false;
  }

  const homeownerId = record?.p_home_owner_id ?? record?.home_owner?.user_id;
  return homeownerId != null && auth.userId != null && Number(homeownerId) === Number(auth.userId);
}

export function getContractSignerType(record: any): "homeowner" | "contractor" {
  return isHomeownerContractOwner(record) ? "homeowner" : "contractor";
}
